import { toast } from "sonner";
import { useSwapAccount } from "../use-swap-account";
import { useTokenStore } from "@/lib/zustand";
import { useUserSettingsStore } from "@/lib/zustand";
import { useSwapTransactions } from "../use-swap-transactions";
import { useClient as useParaClient } from "@getpara/react-sdk";
import {
  getRoutes,
  getStepTransaction,
  executeRoute,
  getActiveRoute,
  RouteExtended,
  ChainType,
  BalanceError,
  type ExecutionOptions,
  type ProcessType,
} from "@lifi/sdk";
import { parseUnits } from "viem";
import { SWAP_FEE } from "@/config";
import { Connection, VersionedTransaction } from "@solana/web3.js";
import { useAccount as useWagmiAccount } from "wagmi";
import { isSolanaAddress } from "@/lib/solana";
import { useQueryClient } from "@tanstack/react-query";
import { swapInProgressRef } from "@/components/providers/lifi-sdk-solana-provider";
import { usePostSwapSync } from "../use-post-swap-sync";
import { refreshSolanaBalances } from "../use-token-balances";
import { isNativeToken } from "@/constants/send-modal-helpers";
import { readAndStoreOverride } from "@/lib/on-chain-balance-reader";
import { mobileDebugLog } from "@/lib/mobile-debug-log";
import { logDiagnostic } from "@/lib/silent-diagnostic";
import { pushHistoryEntry } from "@/lib/transaction-history";
// TEMPORARY (2025-06): Token-2022 routing via OKX until LiFi supports transfer hooks
import {
  shouldRouteThroughOkx,
  prepareOkxToken2022Swap,
  PLATFORM_FEE_BPS,
  getClientSolanaRpcUrl,
} from "@/lib/okx-token2022-routing";

const CROSS_CHAIN_ROUTE_TIMEOUT_MS = 45_000;
const SAME_CHAIN_ROUTE_TIMEOUT_MS = 90_000; // 90s — LiFi route lookup should never take this long
const PARA_INIT_TIMEOUT_MS = 60_000; // 60s — Para server-side init should be fast

// Process types that indicate an actual swap/bridge tx was submitted (not just approval)
// TOKEN_ALLOWANCE and PERMIT are pre-swap approval steps — their txHash doesn't mean swap succeeded
const SWAP_PROCESS_TYPES: ProcessType[] = ['SWAP', 'CROSS_CHAIN', 'RECEIVING_CHAIN'];

// Helper: get native DEX link for EVM chains
function getEvmDexLink(
  chainId: number | string,
  tokenAddress: string
): { url: string; label: string } | null {
  const numericChainId = typeof chainId === "string" ? parseInt(chainId) : chainId;
  switch (numericChainId) {
    case 56: // BSC
      return {
        url: `https://pancakeswap.finance/swap?outputCurrency=${tokenAddress}`,
        label: "Open PancakeSwap",
      };
    case 1: // Ethereum
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${tokenAddress}`,
        label: "Open Uniswap",
      };
    case 8453: // Base
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${tokenAddress}&chain=base`,
        label: "Open Uniswap",
      };
    case 42161: // Arbitrum
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${tokenAddress}&chain=arbitrum`,
        label: "Open Uniswap",
      };
    case 137: // Polygon
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${tokenAddress}&chain=polygon`,
        label: "Open Uniswap",
      };
    case 10: // Optimism
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${tokenAddress}&chain=optimism`,
        label: "Open Uniswap",
      };
    default:
      return null;
  }
}

// Parse canonical fromAmount from LiFi's "fromAmount must be equal to constant <number>" error
// LiFi server computes amounts with slightly different precision; they tell us the correct value
const parseCanonicalAmountFromError = (error: any): string | null => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "";
  // Match pattern: "fromAmount must be equal to constant 123456789"
  const match = message.match(/fromAmount must be equal to constant\s+(\d+)/i);
  if (match?.[1]) {
    console.log("[LiFi] Parsed canonical fromAmount from error:", match[1]);
    return match[1];
  }
  return null;
};

const isNativeEvmAddress = (address?: string | null) => {
  if (!address) return false;
  const normalized = address.toLowerCase();
  return (
    normalized === "0x0000000000000000000000000000000000000000" ||
    normalized === "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"
  );
};

/**
 * Detect insufficient funds / gas errors from various wallet and RPC error formats
 */
const isInsufficientFundsError = (error: unknown): boolean => {
  const message = String(
    (error as any)?.response?.data?.message ||
    (error as any)?.response?.data?.error ||
    (error as any)?.message ||
    ""
  ).toLowerCase();

  return (
    message.includes("insufficient funds") ||
    message.includes("insufficient balance") ||
    message.includes("exceeds balance") ||
    message.includes("transfer amount exceeds balance") ||
    message.includes("gas required exceeds allowance") ||
    message.includes("exceeds the balance")
  );
};

/**
 * Get the native gas token symbol for a given chain ID
 */
const getNativeTokenSymbol = (chainId: number): string => {
  const ETH_CHAINS = [1, 8453, 42161, 10, 59144, 534352, 81457, 324]; // mainnet, base, arb, op, linea, scroll, blast, zksync
  const BNB_CHAINS = [56]; // BSC
  const MATIC_CHAINS = [137]; // Polygon
  const AVAX_CHAINS = [43114]; // Avalanche
  const FTM_CHAINS = [250]; // Fantom
  const XDAI_CHAINS = [100]; // Gnosis
  const MNT_CHAINS = [5000]; // Mantle

  if (ETH_CHAINS.includes(chainId)) return "ETH";
  if (BNB_CHAINS.includes(chainId)) return "BNB";
  if (MATIC_CHAINS.includes(chainId)) return "MATIC";
  if (AVAX_CHAINS.includes(chainId)) return "AVAX";
  if (FTM_CHAINS.includes(chainId)) return "FTM";
  if (XDAI_CHAINS.includes(chainId)) return "xDAI";
  if (MNT_CHAINS.includes(chainId)) return "MNT";
  return "native token"; // fallback
};

const tokenAddressesMatch = ({
  expected,
  actual,
  chainType,
}: {
  expected?: string;
  actual?: string;
  chainType: ChainType;
}) => {
  if (!expected || !actual) return false;

  if (chainType === ChainType.EVM) {
    if (isNativeEvmAddress(expected) && isNativeEvmAddress(actual)) {
      return true;
    }

    return expected.toLowerCase() === actual.toLowerCase();
  }

  return expected === actual;
};

interface UseHandleSwapProps {
  isSwapDisabled: boolean | undefined;
  swapDisabledReason: string | null;
  userEvmBalance: bigint | undefined;
  solanaBalance: number | undefined;
  isSVMtoEvm: boolean;
  evmAddress: string | null;
  solanaAddress: string | null;
  isEvmToSVM: boolean;
  setRouteProcess: (process: any) => void;
  toToken: any;
  setIsLoadingSwap: (isLoading: boolean) => void;
  refreshBalance: () => Promise<unknown>;
  invalidateQueries: () => void;
}

export const useHandleSwap = ({
  isSwapDisabled,
  swapDisabledReason,
  userEvmBalance,
  solanaBalance,
  isSVMtoEvm,
  evmAddress,
  solanaAddress,
  isEvmToSVM,
  setRouteProcess,
  toToken,
  setIsLoadingSwap,
  refreshBalance,
  invalidateQueries,
}: UseHandleSwapProps) => {
  const queryClient = useQueryClient();
  const { address, walletType } = useSwapAccount();
  const { address: wagmiAddress } = useWagmiAccount();
  const { fromToken, fromChain, toChain, fromTokenAmount } = useTokenStore();
  const { userSettings } = useUserSettingsStore();
  const { onSendTransaction } = useSwapTransactions();
  const para = useParaClient();

  // Detect external Solana wallet (Phantom/Solflare) by publicKey matching
  // Para's profileBalance doesn't track external wallets, so we need different sync behavior
  const isExternalSolana = (() => {
    if (typeof window === "undefined") return false;
    if (fromChain?.chainType !== ChainType.SVM) return false;

    const solanaWallet = (window as any).solana;
    const solflareWallet = (window as any).solflare;
    const connectedPubkey = address;

    const phantomPubkey = solanaWallet?.publicKey?.toString();
    const solflarePubkey = solflareWallet?.publicKey?.toString();

    const isPhantomConnected = !!solanaWallet?.isPhantom && phantomPubkey === connectedPubkey;
    const isSolflareConnected = !!solflareWallet && solflarePubkey === connectedPubkey;

    return isPhantomConnected || isSolflareConnected;
  })();

  const { triggerPostSwapSync, captureSnapshot } = usePostSwapSync({
    fromChainId: fromChain?.id,
    toChainId: toChain?.id,
    isExternalSolana,
    evmAddress: wagmiAddress || evmAddress,
  });

  const getTransactionLinks = (route: RouteExtended, onlyIfDone = false) => {
    route.steps.forEach((step) => {
      // Only set routeProcess if step is DONE (not FAILED or in-progress)
      if (onlyIfDone && step.execution?.status !== "DONE") {
        return;
      }
      step.execution?.process.forEach((process) => {
        if (process.txHash && process.status === "DONE") {
          setRouteProcess(process);
        }
      });
    });
  };

  const handleSwap = async () => {
    logDiagnostic({ event: "swap-initiated", fromSymbol: fromToken?.symbol, toSymbol: toToken?.symbol, fromChainId: fromChain?.id, toChainId: toChain?.id, amount: fromTokenAmount });

    // Early validation checks
    if (!address) {
      toast.error("Please connect your wallet");
      return;
    }

    if (!fromTokenAmount || fromTokenAmount === "0") {
      toast.error("Please enter an amount greater than 0");
      return;
    }

    if (isSwapDisabled) {
      toast.error(swapDisabledReason || "An unexpected error occurred");
      return;
    }

    // Balance validation
    const hasInsufficientBalance =
      fromChain.chainType === ChainType.EVM
        ? userEvmBalance && Number(fromTokenAmount) > Number(userEvmBalance)
        : solanaBalance && Number(fromTokenAmount) > Number(solanaBalance);

    if (hasInsufficientBalance) {
      toast.error("Insufficient balance");
      return;
    }

    // Validate toAddress based on chain type
    if (isSVMtoEvm) {
      if (
        !evmAddress ||
        !evmAddress.startsWith("0x") ||
        evmAddress.length !== 42
      ) {
        toast.error("Please enter a valid EVM address");
        return;
      }
    } else if (isEvmToSVM) {
      if (!solanaAddress || !isSolanaAddress(solanaAddress)) {
        toast.error("Please enter a valid Solana address");
        return;
      }
    } else if (!address) {
      toast.error("Please connect your wallet");
      return;
    }

    const isCrossChainSwap = fromChain.id !== toChain.id;
    const involvesEthereum = fromChain.id === 1 || toChain.id === 1;

    // Show expectation-setting message for slow chains
    if (involvesEthereum || isCrossChainSwap) {
      toast.info(
        involvesEthereum
          ? "Ethereum swaps can take 2-5 minutes to confirm"
          : "Cross-chain swaps may take a few minutes",
        { duration: 4000 }
      );
    }

    const swapStartTime = Date.now();
    toast.loading("Processing your swap... (0s)", { id: "swap-progress" });

    // Update elapsed time every second
    const elapsedInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - swapStartTime) / 1000);
      const mins = Math.floor(elapsed / 60);
      const secs = elapsed % 60;
      const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      const message = isCrossChainSwap
        ? `Processing your swap... waiting for bridge (${timeStr})`
        : `Processing your swap... (${timeStr})`;
      toast.loading(message, { id: "swap-progress" });
    }, 1000);

    try {
      setIsLoadingSwap(true);

      const swapFromAddress =
        fromChain?.chainType === ChainType.EVM && wagmiAddress
          ? wagmiAddress
          : address;
      const swapToAddress: any = isSVMtoEvm
        ? evmAddress
        : isEvmToSVM
          ? solanaAddress
          : toChain?.chainType === ChainType.EVM && wagmiAddress
            ? wagmiAddress
            : address;

      // =========================================================================
      // TEMPORARY (2025-06): Token-2022 routing via OKX
      // LiFi doesn't resolve Token-2022 transfer-hook accounts, so we route
      // Token-2022 swaps through OKX DEX API which handles hooks automatically.
      // Delete this block when LiFi adds Token-2022 transfer-hook support.
      // =========================================================================
      const isSameChainSolana = fromChain.chainType === ChainType.SVM && !isCrossChainSwap;
      let useOkxForToken2022 = false;

      console.log(`[ROUTE DECISION] isSameChainSolana=${isSameChainSolana}, fromToken=${fromToken?.address}, toToken=${toToken?.address}`);

      if (isSameChainSolana && fromToken?.address && toToken?.address) {
        try {
          const connection = new Connection(getClientSolanaRpcUrl());
          useOkxForToken2022 = await shouldRouteThroughOkx(
            connection,
            fromToken.address,
            toToken.address
          );
          console.log(`[ROUTE DECISION] shouldRouteThroughOkx returned: ${useOkxForToken2022}`);
          if (useOkxForToken2022) {
            logDiagnostic({ event: "token2022-detected", router: "okx" });
          }
        } catch (e) {
          console.error("[ROUTE DECISION] shouldRouteThroughOkx THREW ERROR:", e);
          console.warn("[Token-2022 check] Failed, defaulting to LiFi:", e);
        }
      } else {
        console.log("[ROUTE DECISION] Skipping Token-2022 check (not same-chain Solana or missing token addresses)");
      }

      // Token-2022 OKX swap path
      if (useOkxForToken2022) {
        console.log("[OKX PATH] entering - Token-2022 detected, using OKX");
        logDiagnostic({ event: "okx-swap-start" });
        const connection = new Connection(getClientSolanaRpcUrl());
        // OKX uses slippage as decimal percentage (0.5 = 0.5%), not basis points
        const slippagePercent = Number(userSettings.slippage);
        const amountInBaseUnits = parseUnits(
          fromTokenAmount || "0",
          fromToken?.decimals || 9
        ).toString();

        const okxResult = await prepareOkxToken2022Swap(
          connection,
          swapFromAddress,
          fromToken!.address,
          toToken!.address,
          amountInBaseUnits,
          slippagePercent
        );

        console.log("[OKX Token-2022] Sending transaction...");
        const signature = await onSendTransaction(okxResult.transaction);
        logDiagnostic({ event: "okx-swap-sent", signature: signature.slice(0, 16) });

        // Log OKX swap to database (fire-and-forget)
        try {
          const isPhantom = typeof window !== "undefined" && !!(window as any).solana?.isPhantom;
          const detectedWalletType = isPhantom ? "phantom" : "para_internal";

          fetch("/api/swaps/log", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              txHash: signature,
              chain: "solana",
              fromChainId: fromChain.id,
              toChainId: toChain.id,
              fromTokenAddress: fromToken?.address,
              toTokenAddress: toToken?.address,
              fromTokenSymbol: fromToken?.symbol,
              toTokenSymbol: toToken?.symbol,
              fromAmount: fromTokenAmount,
              toAmount: okxResult.outputAmount,
              amountUsd: null, // Server will calculate
              walletAddress: address,
              walletType: detectedWalletType,
              // TEMPORARY: Mark as OKX-routed for reconciliation
              router: "okx",
              feeMint: okxResult.feeMint,
            }),
          }).catch((e) => console.warn("[Swap Log] Failed:", e));
        } catch (e) {
          console.warn("[Swap Log] Error:", e);
        }

        // Format output amount for display
        const outAmountFormatted = (
          Number(okxResult.outputAmount) / Math.pow(10, toToken?.decimals || 9)
        ).toFixed(6);

        clearInterval(elapsedInterval);
        toast.dismiss("swap-progress");

        toast.success(
          `Swapped ${fromTokenAmount} ${fromToken?.symbol} for ~${outAmountFormatted} ${toToken?.symbol}`,
          { duration: 5000 }
        );

        // Track in history
        pushHistoryEntry({
          type: "swap",
          hash: signature,
          status: "success",
          chainId: fromChain.id,
          fromSymbol: fromToken?.symbol ?? "?",
          fromAmount: fromTokenAmount || "0",
          toSymbol: toToken?.symbol ?? "?",
          toAmount: outAmountFormatted,
        });

        // Post-swap sync
        try {
          await triggerPostSwapSync();
        } catch (syncError) {
          console.warn("[Post-swap sync] Error:", syncError);
        }

        // Balance refresh
        if (fromChain.chainType === ChainType.SVM && address) {
          refreshSolanaBalances();
          queryClient.invalidateQueries({ queryKey: ["para-balance"] });
        }

        setIsLoadingSwap(false);
        return; // Exit early - OKX path complete
      }
      // =========================================================================
      // END TEMPORARY Token-2022 block
      // =========================================================================

      console.log("[LIFI PATH] entering - using LiFi router");
      const routeRequest = {
        fromChainId: fromChain.id,
        toChainId: toChain.id,
        fromTokenAddress: fromToken?.address,
        toTokenAddress: toToken?.address,
        fromAmount: parseUnits(
          fromTokenAmount || "0",
          fromToken?.decimals || 18,
        ).toString(),
        fromAddress: swapFromAddress,
        toAddress: swapToAddress,
        options: {
          fee: SWAP_FEE,
          slippage: Number(userSettings.slippage),
          // Disable LiFi's default 10% price impact ceiling. Stale price feeds for low-cap tokens
          // cause false high-impact rejections; the actual swap executes at Jupiter's real market rate.
          maxPriceImpact: 1,
        },
      };

      // Get and validate routes
      logDiagnostic({ event: "swap-routes-start", isCrossChain: isCrossChainSwap });
      const routesResponse = await Promise.race([
        getRoutes(routeRequest),
        new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error(
              isCrossChainSwap
                ? "Route discovery timed out."
                : "Route lookup is slow. Please try again."
            ));
          }, isCrossChainSwap ? CROSS_CHAIN_ROUTE_TIMEOUT_MS : SAME_CHAIN_ROUTE_TIMEOUT_MS);
        }),
      ]);
      logDiagnostic({ event: "swap-routes-done", routeCount: routesResponse?.routes?.length ?? 0 });

      if (!routesResponse.routes?.length) {
        // Debug: capture full routes response to diagnose tax token detection
        const respObj = routesResponse as Record<string, unknown>;
        console.log("[Routes Error Debug]", {
          routes: respObj?.routes,
          unavailableRoutes: respObj?.unavailableRoutes,
          filteredOut: (respObj?.unavailableRoutes as Record<string, unknown>)?.filteredOut,
          failed: (respObj?.unavailableRoutes as Record<string, unknown>)?.failed,
          fullResponse: JSON.stringify(routesResponse),
        });

        // Differentiate EVM vs Solana errors - EVM "no route" is usually unroutable token (tax/unsupported)
        const isEvmSwap = fromChain.chainType === ChainType.EVM;
        if (isEvmSwap && !isCrossChainSwap) {
          // EVM same-chain swap with no route = likely unroutable token
          // Throw special marker error so UI can show DEX fallback link
          throw new Error("UNROUTABLE_EVM_TOKEN");
        } else if (isCrossChainSwap) {
          throw new Error("No route found for this token pair");
        } else {
          // Solana same-chain - slippage advice is appropriate
          throw new Error("No route found. Try increasing slippage to 3-5% in settings, or the token may have insufficient liquidity.");
        }
      }

      const validRoutes = isCrossChainSwap
        ? routesResponse.routes.filter((route) =>
            tokenAddressesMatch({
              expected: toToken?.address,
              actual: route.toToken?.address,
              chainType: toChain.chainType,
            }),
          )
        : routesResponse.routes;

      if (!validRoutes.length) {
        throw new Error("No route found for this token pair");
      }

      let result: {
        message: string;
        txHash?: string;
        completionTime: string;
      };

      if (fromChain.chainType === ChainType.SVM) {
        const route = validRoutes[0];

        if (
          isCrossChainSwap &&
          !tokenAddressesMatch({
            expected: toToken?.address,
            actual: route.toToken?.address,
            chainType: toChain.chainType,
          })
        ) {
          throw new Error("No route found for this token pair");
        }

        const stepWithTxData = await getStepTransaction(route.steps[0]);
        if (!stepWithTxData.transactionRequest) {
          throw new Error(
            "No transaction request found for this swap. Try increasing slippage or trying another pair.",
          );
        }

        const transactionData = stepWithTxData.transactionRequest.data;
        const transaction = VersionedTransaction.deserialize(
          Buffer.from(transactionData as string, "base64"),
        );

        const signature = await onSendTransaction(transaction);

        try {
          await fetch(
            `https://li.quest/v1/status?fromChain=1151111081099710&toChain=${toChain.id}&txHash=${signature}&bridge=jupiter`,
          );
        } catch (e) {
          // Non-critical: don't block successful swaps if status indexing ping fails.
          console.warn("LiFi status ping failed:", e);
        }

        result = {
          message: "Swap executed successfully!",
          txHash: signature,
          completionTime: new Date().toISOString(),
        };

        // Log Solana swap to database (fire-and-forget)
        try {
          // Detect wallet type: Para internal vs Phantom vs other external
          const isPhantom = typeof window !== "undefined" && !!(window as any).solana?.isPhantom;
          const detectedWalletType = isPhantom ? "phantom" : "para_internal";

          fetch("/api/swaps/log", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              txHash: signature,
              chain: "solana",
              fromChainId: fromChain.id,
              toChainId: toChain.id,
              fromTokenAddress: fromToken?.address,
              toTokenAddress: toToken?.address,
              fromTokenSymbol: fromToken?.symbol,
              toTokenSymbol: toToken?.symbol,
              fromAmount: fromTokenAmount,
              toAmount: route.toAmount,
              amountUsd: route.toAmountUSD || null,
              walletAddress: address,
              walletType: detectedWalletType,
            }),
          }).catch((e) => console.warn("[Swap Log] Failed:", e));
        } catch (e) {
          console.warn("[Swap Log] Error:", e);
        }
      } else {
        const recommendedRoute =
          validRoutes.find((route) =>
            route.tags?.includes("RECOMMENDED"),
          ) || validRoutes[0];

        if (!recommendedRoute) {
          throw new Error("No valid routes available. Please try again.");
        }

        if (
          isCrossChainSwap &&
          !tokenAddressesMatch({
            expected: toToken?.address,
            actual: recommendedRoute.toToken?.address,
            chainType: toChain.chainType,
          })
        ) {
          throw new Error("No route found for this token pair");
        }
        // Prevent setProviders from running during swap execution
        swapInProgressRef.current = true;

        // Track txHash from updateRouteHook - if we capture one before an error,
        // the transaction was submitted on-chain successfully
        let mainFlowTxHash: string | null = null;

        // Capture pre-swap snapshot for quantity-based stability detection
        if (fromToken && toToken && fromChain && toChain) {
          captureSnapshot({
            fromToken: {
              address: fromToken.address || "",
              symbol: fromToken.symbol,
              chainId: fromChain.id,
              isNative: isNativeToken(fromToken.symbol),
            },
            toToken: {
              address: toToken.address || "",
              symbol: toToken.symbol,
              chainId: toChain.id,
              isNative: isNativeToken(toToken.symbol),
            },
          });
        }

        const resultRoute = await new Promise<RouteExtended>(
          async (resolve, reject) => {
            try {
              const executionSettings: ExecutionOptions = {
                infiniteApproval: false,
                updateTransactionRequestHook: async (txRequest) => {
                  try {
                    const boost = (val: bigint) => val + val / BigInt(5);

                    if (txRequest.gasPrice) {
                      txRequest.gasPrice = boost(BigInt(txRequest.gasPrice));
                    }
                    if (txRequest.maxFeePerGas) {
                      txRequest.maxFeePerGas = boost(
                        BigInt(txRequest.maxFeePerGas),
                      );
                    }
                    if (txRequest.maxPriorityFeePerGas) {
                      txRequest.maxPriorityFeePerGas = boost(
                        BigInt(txRequest.maxPriorityFeePerGas),
                      );
                    }
                  } catch (e) {
                    console.warn("Gas boost failed, using original values:", e);
                  }

                  return txRequest;
                },
              };

              // Timeout promise - reject after 10 minutes (approval + swap + confirmation can be slow on mainnet)
              const SWAP_TIMEOUT_MS = 600000;
              const timeoutPromise = new Promise<never>((_, timeoutReject) => {
                setTimeout(() => {
                  timeoutReject(new Error("Swap timed out, please try again"));
                }, SWAP_TIMEOUT_MS);
              });

              // Hydrate Para wallet cache before executeRoute (#590 bug workaround)
              // init() is idempotent and ensures getWalletsByType('EVM') returns wallets
              if (para) {
                logDiagnostic({ event: "swap-para-init-start" });
                await Promise.race([
                  para.init(),
                  new Promise<never>((_, reject) =>
                    setTimeout(
                      () => reject(new Error("Para signing service is slow. Please try again.")),
                      PARA_INIT_TIMEOUT_MS
                    )
                  ),
                ]);
                logDiagnostic({ event: "swap-para-init-done" });
              }

              // Fetch fresh route right before executeRoute to avoid stale route IDs
              const freshRoutesResponse = await getRoutes(routeRequest);
              const freshRoute =
                freshRoutesResponse.routes?.find(
                  (r) => r.steps?.[0]?.tool === recommendedRoute.steps?.[0]?.tool
                ) || freshRoutesResponse.routes?.[0];

              if (!freshRoute) {
                throw new Error("Failed to fetch fresh route for execution");
              }

              // Execute route with timeout
              logDiagnostic({ event: "swap-execute-start", toolUsed: freshRoute?.steps?.[0]?.tool || "unknown" });
              const route = await Promise.race([
                executeRoute(freshRoute, {
                  updateRouteHook(route) {
                    // Capture txHash only from SWAP/CROSS_CHAIN/RECEIVING_CHAIN processes
                    // TOKEN_ALLOWANCE (approval) txHash should NOT count as "swap succeeded"
                    const txHash = route.steps
                      .flatMap((step) => step.execution?.process || [])
                      .find((p) => p.txHash && SWAP_PROCESS_TYPES.includes(p.type))?.txHash;
                    if (txHash) {
                      mainFlowTxHash = txHash;
                    }

                    const firstStep = route.steps[0];
                    const isCrossChain = fromChain.id !== toChain.id;

                    const allStepsDone = route.steps.every(
                      (step) => step.execution?.status === "DONE",
                    );
                    const anyStepFailed = route.steps.some(
                      (step) => step.execution?.status === "FAILED",
                    );

                    if (anyStepFailed) {
                      toast.dismiss("cross-chain-progress");

                      // Check ALL processes across ALL steps for swap/bridge txHash (not approvals)
                      const allProcesses = route.steps.flatMap((step) => step.execution?.process || []);
                      const anyTxHash = allProcesses.find(
                        (p) => p.txHash && SWAP_PROCESS_TYPES.includes(p.type)
                      )?.txHash;

                      // Also check if mainFlowTxHash was captured in a previous hook call
                      const hasTxHash = mainFlowTxHash || anyTxHash;

                      if (hasTxHash) {
                        console.log("[LiFi] Step marked FAILED but txHash exists, treating as success:", hasTxHash);
                        if (anyTxHash) mainFlowTxHash = anyTxHash; // Ensure it's captured
                        getTransactionLinks(route, false);
                        resolve(route);
                        return;
                      }

                      // Log full execution details for failed steps (only if no txHash anywhere)
                      console.error("[LiFi] No txHash found, this is a real failure");
                      console.error("[LiFi] All processes:", allProcesses);
                      const failedSteps = route.steps.filter(
                        (step) => step.execution?.status === "FAILED",
                      );
                      failedSteps.forEach((step, idx) => {
                        const failedProcess = step.execution?.process?.find(
                          (p) => p.status === "FAILED",
                        );
                        console.error(`[LiFi] Step ${idx} failed:`, step);
                        console.error(`[LiFi] Failed process:`, failedProcess);
                        console.error(`[LiFi] Step ${idx} full details:`, {
                          tool: step.tool,
                          type: step.type,
                          action: step.action,
                          estimate: step.estimate,
                          execution: step.execution,
                          allProcesses: step.execution?.process,
                        });
                      });
                      // Don't reject here — let executeRoute throw and the catch block handle it
                      // This allows canonical-amount retry to work correctly
                    } else if (allStepsDone) {
                      // Only set routeProcess when all steps are DONE (not failed)
                      getTransactionLinks(route, true);
                      toast.dismiss("cross-chain-progress");
                      resolve(route);
                    } else if (isCrossChain && firstStep.execution?.status === "DONE") {
                      // Bridge leg done, destination swap still in progress — do not resolve yet
                      console.log(
                        "Bridge complete, waiting for destination swap to finish...",
                      );
                      toast.loading(
                        "Bridge complete! Waiting for destination swap to finish. This may take a few minutes...",
                        {
                          id: "cross-chain-progress",
                          duration: 600000,
                        },
                      );
                    }
                  },
                  ...executionSettings,
                }),
                timeoutPromise,
              ]);
              logDiagnostic({ event: "swap-execute-done", hasResult: !!route });
              resolve(route);
            } catch (error: any) {
              // If we captured a txHash, transaction was submitted on-chain successfully
              // Resolve as success even if status polling returned 404
              if (mainFlowTxHash) {
                console.log("[LiFi] Transaction submitted on-chain, txHash:", mainFlowTxHash);
                resolve({
                  steps: [{
                    execution: {
                      status: "DONE",
                      process: [{ txHash: mainFlowTxHash, status: "DONE" }],
                    },
                  }],
                } as any);
                return;
              }

              // Check for insufficient funds/gas errors BEFORE canonical-retry logic
              // Skip retry attempts - these errors are unrecoverable
              // Toast will be shown by outer catch block with chain-aware symbol
              if (isInsufficientFundsError(error)) {
                reject(error);
                return;
              }

              // Check if this is the "fromAmount must be equal to constant" error
              // LiFi server computes amounts with different precision - retry with their canonical value
              const canonicalAmount = parseCanonicalAmountFromError(error);
              if (canonicalAmount && canonicalAmount !== routeRequest.fromAmount) {
                // Track txHash from updateRouteHook - if we get one before a 404 error,
                // the transaction was submitted successfully
                let capturedTxHash: string | undefined;

                try {
                  // Re-fetch routes with the canonical amount
                  const retryRequest = { ...routeRequest, fromAmount: canonicalAmount };

                  // Create new timeout for retry (10 minutes)
                  const retryTimeoutPromise = new Promise<never>((_, timeoutReject) => {
                    setTimeout(() => {
                      timeoutReject(new Error("Swap timed out on retry, please try again"));
                    }, 600000);
                  });

                  // Fetch fresh route right before executeRoute
                  const freshRetryResponse = await getRoutes(retryRequest);

                  if (!freshRetryResponse.routes?.length) {
                    throw new Error("No routes found on retry with canonical amount");
                  }

                  const freshRetryValidRoutes = isCrossChainSwap
                    ? freshRetryResponse.routes.filter((route) =>
                        tokenAddressesMatch({
                          expected: toToken?.address,
                          actual: route.toToken?.address,
                          chainType: toChain.chainType,
                        }),
                      )
                    : freshRetryResponse.routes;

                  const freshRetryRoute =
                    freshRetryValidRoutes.find((r) => r.tags?.includes("RECOMMENDED")) ||
                    freshRetryValidRoutes[0];

                  if (!freshRetryRoute) {
                    throw new Error("No valid route on retry");
                  }

                  // Execute with fresh retry route
                  const retryResult = await Promise.race([
                    executeRoute(freshRetryRoute, {
                      updateRouteHook(route) {
                        // Capture txHash only from swap/bridge processes, not approvals
                        const txHash = route.steps
                          .flatMap((step) => step.execution?.process || [])
                          .find((p) => p.txHash && SWAP_PROCESS_TYPES.includes(p.type))?.txHash;
                        if (txHash) {
                          capturedTxHash = txHash;
                        }

                        const allStepsDone = route.steps.every(
                          (step) => step.execution?.status === "DONE",
                        );
                        if (allStepsDone) {
                          toast.dismiss("cross-chain-progress");
                        }
                      },
                      infiniteApproval: false,
                      acceptExchangeRateUpdateHook: async () => true,
                    }),
                    retryTimeoutPromise,
                  ]);

                  // Check if retry succeeded - look for txHash as proof of on-chain submission
                  const retryTxHash = capturedTxHash || retryResult.steps
                    .flatMap((step) => step.execution?.process || [])
                    .find((p) => p.txHash)?.txHash;

                  const allStepsDone = retryResult.steps.every(
                    (step) => step.execution?.status === "DONE",
                  );
                  const anyStepFailed = retryResult.steps.some(
                    (step) => step.execution?.status === "FAILED",
                  );

                  // If we have a txHash, transaction was submitted - treat as success
                  // (status polling may return 404 for new transactions)
                  if (retryTxHash || allStepsDone) {
                    getTransactionLinks(retryResult, true);
                    resolve(retryResult);
                    return;
                  }

                  // Only fail if explicitly failed AND no swap/bridge txHash exists
                  if (anyStepFailed) {
                    // Double-check for swap/bridge txHash in ALL processes before rejecting (not approvals)
                    const allRetryProcesses = retryResult.steps.flatMap((step) => step.execution?.process || []);
                    const anyRetryTxHash = capturedTxHash || allRetryProcesses.find(
                      (p) => p.txHash && SWAP_PROCESS_TYPES.includes(p.type)
                    )?.txHash;

                    if (anyRetryTxHash) {
                      getTransactionLinks(retryResult, false);
                      resolve(retryResult);
                      return;
                    }

                    const failedStep = retryResult.steps.find(
                      (step) => step.execution?.status === "FAILED",
                    );
                    reject(new Error(`Swap failed during retry: ${failedStep?.execution?.process?.find(p => p.status === "FAILED")?.message || "Unknown error"}`));
                    return;
                  }

                  // No txHash and not done - resolve anyway, transaction may still be pending
                  resolve(retryResult);
                  return;
                } catch (retryError: any) {
                  // If we captured a txHash before the error, transaction was submitted
                  // Successfully on-chain - treat as success despite the error
                  if (capturedTxHash) {
                    console.log("[LiFi] Retry threw error but txHash was captured:", capturedTxHash);
                    // Create minimal route object with the captured txHash for downstream processing
                    resolve({
                      steps: [{
                        execution: {
                          status: "DONE",
                          process: [{ txHash: capturedTxHash, status: "DONE" }],
                        },
                      }],
                    } as any);
                    return;
                  }

                  console.error("[LiFi] Retry also failed:", retryError);
                  const retryErrorMessage = retryError?.response?.data?.message
                    || retryError?.response?.data?.error
                    || retryError?.message
                    || "Retry failed";
                  reject(new Error(`LiFi execution failed after retry: ${retryErrorMessage}`));
                  return;
                }
              }

              // If it's an axios error with response body, include that in the rejection
              const apiErrorMessage = error?.response?.data?.message
                || error?.response?.data?.error
                || error?.message
                || "Unknown LiFi error";
              reject(new Error(`LiFi execution failed: ${apiErrorMessage}`));
            }
          },
        );

        // Swap execution complete, allow setProviders again
        swapInProgressRef.current = false;

        const lastStepWithExecution = [...resultRoute.steps]
          .reverse()
          .find((step) => step.execution);
        const latestProcess = lastStepWithExecution?.execution?.process.find(
          (p) => p.txHash,
        );

        const completionTime =
          typeof latestProcess?.doneAt === "number"
            ? new Date(latestProcess.doneAt).toISOString()
            : new Date().toISOString();

        result = {
          message:
            fromChain.id !== toChain.id
              ? "Swap complete! Check the tx link below."
              : "Swap complete!",
          txHash: latestProcess?.txHash,
          completionTime,
        };

        // Log EVM swap to database (fire-and-forget)
        try {
          // Detect wallet type based on wagmi connector or Para internal
          const isMetaMask = typeof window !== "undefined" && !!(window as any).ethereum?.isMetaMask;
          const detectedWalletType = wagmiAddress
            ? (isMetaMask ? "metamask" : "other_external")
            : "para_internal";

          fetch("/api/swaps/log", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              txHash: latestProcess?.txHash,
              chain: fromChain.name?.toLowerCase() || "ethereum",
              fromChainId: fromChain.id,
              toChainId: toChain.id,
              fromTokenAddress: fromToken?.address,
              toTokenAddress: toToken?.address,
              fromTokenSymbol: fromToken?.symbol,
              toTokenSymbol: toToken?.symbol,
              fromAmount: fromTokenAmount,
              toAmount: resultRoute.toAmount,
              amountUsd: resultRoute.toAmountUSD || null,
              walletAddress: wagmiAddress || address,
              walletType: detectedWalletType,
            }),
          }).catch((e) => console.warn("[Swap Log] Failed:", e));
        } catch (e) {
          console.warn("[Swap Log] Error:", e);
        }
      }

      toast.success(result.message, {
        duration: 6000,
      });

      // Trigger post-swap balance sync with stability detection
      logDiagnostic({ event: "swap-completed-successfully" });
      triggerPostSwapSync();

      // Trigger Solana on-chain balance refresh for SOL swaps
      // Uses React Query cache invalidation - all components share the cache
      // and will refetch ONCE (shared) then re-render together
      if (fromChain.chainType === ChainType.SVM) {
        logDiagnostic({ event: "sol-swap-balance-refresh-start" });
        try {
          refreshSolanaBalances(queryClient);
          logDiagnostic({ event: "sol-swap-balance-refresh-done" });
        } catch (e) {
          console.warn("[Solana] Balance refresh trigger failed:", e);
        }
      }

      // Fire-and-forget on-chain balance reads for swap tokens
      // These override Para's potentially stale data with authoritative on-chain values
      // USD values are computed at injection time using Para's current prices
      const userEvmAddress = evmAddress || wagmiAddress;

      // Diagnostic: capture every variable's state before gate check
      logDiagnostic({
        event: "swap-override-gate-check",
        hasFromToken: !!fromToken,
        hasToToken: !!toToken,
        hasFromChain: !!fromChain,
        hasToChain: !!toChain,
        hasEvmAddress: !!evmAddress,
        hasWagmiAddress: !!wagmiAddress,
        evmAddressPrefix: evmAddress ? String(evmAddress).slice(0, 8) : null,
        wagmiAddressPrefix: wagmiAddress ? String(wagmiAddress).slice(0, 8) : null,
        fromTokenSymbol: fromToken?.symbol || null,
        toTokenSymbol: toToken?.symbol || null,
        fromChainId: fromChain?.id || null,
        toChainId: toChain?.id || null,
      });

      if (fromToken && toToken && fromChain && toChain && userEvmAddress) {
        logDiagnostic({ event: "swap-override-gate-passed" });

        mobileDebugLog("SWAP-DONE", "captures started", {
          fromSymbol: fromToken?.symbol,
          toSymbol: toToken?.symbol,
          userAddr: userEvmAddress?.slice(0, 8) + "...",
        });

        readAndStoreOverride(userEvmAddress, {
          address: fromToken.address || "",
          chainId: fromChain.id,
          symbol: fromToken.symbol,
          decimals: fromToken.decimals ?? 18,
          isNative: isNativeToken(fromToken.symbol),
        }).catch(() => { /* Silently ignore override read failures */ });

        readAndStoreOverride(userEvmAddress, {
          address: toToken.address || "",
          chainId: toChain.id,
          symbol: toToken.symbol,
          decimals: toToken.decimals ?? 18,
          isNative: isNativeToken(toToken.symbol),
        }).catch(() => { /* Silently ignore override read failures */ });

        // Push to transaction history after successful swap
        if (result.txHash) {
          pushHistoryEntry({
            type: "swap",
            hash: result.txHash,
            status: "success",
            chainId: fromChain.id,
            fromSymbol: fromToken.symbol,
            fromAmount: fromTokenAmount || "0",
            toSymbol: toToken.symbol,
            // toAmount not reliably available post-execution; user can see in explorer
            toChainId: toChain.id !== fromChain.id ? toChain.id : undefined,
          });
        }
      } else {
        logDiagnostic({
          event: "swap-override-gate-FAILED",
          reason: "missing prerequisite",
        });
      }

      return result;
    } catch (error: any) {
      logDiagnostic({ event: "swap-error", message: error?.message?.slice(0, 200) || "unknown", errorType: error?.constructor?.name || "unknown" });
      console.error("Swap error:", error);

      // Extract the most descriptive error message available
      let message = "An unexpected error occurred";

      if (error instanceof BalanceError) {
        message = "Insufficient funds for this swap.";
      } else if (error?.message?.includes("User rejected")) {
        message = "Transaction rejected by user";
      } else if (error?.response?.data?.message) {
        // LiFi API error with detailed message
        message = error.response.data.message;
      } else if (error?.response?.data?.error) {
        // Alternative API error format
        message = error.response.data.error;
      } else if (error?.message) {
        message = error.message;
      }

      const isSolana = fromChain.chainType === ChainType.SVM;
      const messageLower = message.toLowerCase();

      // Check for insufficient funds/gas errors - use chain-aware native token symbol
      if (isInsufficientFundsError(error)) {
        const nativeSymbol = getNativeTokenSymbol(fromChain.id);
        toast.error(
          `Insufficient balance — make sure you have enough ${nativeSymbol} to cover the swap amount plus gas fees.`,
          { duration: 10000 }
        );
      } else if (message === "UNROUTABLE_EVM_TOKEN") {
        // EVM token can't be routed - show DEX fallback link
        const dexLink = getEvmDexLink(toChain.id, toToken?.address || "");
        if (dexLink) {
          toast.error("This token can't be routed through our aggregator.", {
            action: {
              label: dexLink.label,
              onClick: () => window.open(dexLink.url, "_blank"),
            },
            duration: 12000,
          });
        } else {
          toast.error("This token can't be routed through our aggregator. Try swapping directly on a DEX.", {
            duration: 10000,
          });
        }
      } else if (message.includes("No route found") && isSolana) {
        toast.error(message, {
          action: {
            label: "Open jup.ag",
            onClick: () => window.open("https://jup.ag", "_blank"),
          },
          duration: 10000,
        });
      } else {
        // Show full error message with longer duration for mobile users
        toast.error(message, { duration: 8000 });
      }

      throw error;
    } finally {
      logDiagnostic({ event: "swap-finally-cleanup" });
      // Always reset swap flag to allow setProviders again
      swapInProgressRef.current = false;
      clearInterval(elapsedInterval);
      toast.dismiss("swap-progress");
      setIsLoadingSwap(false);
    }
  };

  return { handleSwap };
};
